/**
 * app-payment-methods-adapter.ts — 将 App 支付 placement 行聚合为固定渠道状态摘要。
 *
 * 状态口径：设 `m` = 该渠道未软删物理行数，`n` = 其中 `adapty_placement_id IS NOT NULL` 的行数
 * （**已配置**行；渠道行本身已无 `is_active` 启停）。
 *
 * **本摘要只回答「placement 配了多少」，不回答「该通道是否启用」** —— 通道启停的唯一真值是通道策略组
 * 的成员集合（ADM-PAY-S008，`02-API契约/A8-运营支付.md` §7.8），由 `PaymentMethodPoliciesPanel` 呈现。
 * 曾经的 `app_disabled` 档取自 `apps.is_iap_enabled` / `is_stripe_enabled`，那两列已随 ADM-APP-S009
 * 废弃、C 端 serving 不再读取（`00-详细需求/30-运营后台需求文档.md` 状态列 `N/A:已废弃`），据其渲染
 * 「App 已关闭」会与策略组给出相反结论，故整档移除：**宁可不说，也不说错**。
 *
 * 仅基于接口返回的物理行计数；不按 scenario 去重或补全，数据库唯一性不由前端 adapter 假设。
 */

import type { AppPaymentMethodResponse } from '@novel-admin/shared';

export type PaymentMethodSummaryStatus = 'unconfigured' | 'active' | 'partial';

export interface PaymentMethodSummary {
  paymentMethod: AppPaymentMethodResponse['payment_method'];
  /** 该渠道未软删物理行数（m）*/
  configuredScenarioCount: number;
  /** 其中已填 placement 的行数（n）——未填 = 该 scenario 下该渠道未配置、不参与 serving 候选 */
  placementScenarioCount: number;
  status: PaymentMethodSummaryStatus;
}

const PAYMENT_METHOD_ORDER = ['IAP', 'STRIPE'] as const;

function derivePaymentMethodSummaryStatus(
  configuredScenarioCount: number,
  placementScenarioCount: number,
): PaymentMethodSummaryStatus {
  // m=0 与「m>0 但一条 placement 都没填」同为「未配置」——两者对 serving 的效果一致。
  if (placementScenarioCount === 0) return 'unconfigured';
  if (placementScenarioCount === configuredScenarioCount) return 'active';
  return 'partial';
}

/** 将真实 payment method 行聚合为 IAP、STRIPE 的固定顺序摘要（只反映 placement 配置度）。 */
export function summarizeAppPaymentMethods(
  methods: AppPaymentMethodResponse[],
): PaymentMethodSummary[] {
  return PAYMENT_METHOD_ORDER.map((paymentMethod) => {
    const methodRows = methods.filter((method) => method.payment_method === paymentMethod);
    const configuredScenarioCount = methodRows.length;
    const placementScenarioCount = methodRows.filter(
      (method) => method.adapty_placement_id !== null,
    ).length;

    return {
      paymentMethod,
      configuredScenarioCount,
      placementScenarioCount,
      status: derivePaymentMethodSummaryStatus(configuredScenarioCount, placementScenarioCount),
    };
  });
}
